import { useEffect, useRef, useState } from 'preact/hooks';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { addDays } from 'shared/checkin-weeks';
import { isOpeningStep, nextSequenceStep, TrackerChannel } from 'shared/tracker-config';
import {
  fmtTrackerDate,
  parseMoneyCents,
  personName,
  sortTouches,
  stageForRole,
  stageLabel,
  stageRole,
} from 'shared/tracker-people';
import {
  AMOUNT_BOOKED_MESSAGE,
  AMOUNT_COLLECTED_MESSAGE,
  BOOK_A_CONVERSATION,
  BOOKING_DATE_MESSAGE,
  FUTURE_DATE_MESSAGE,
  NUMBERS_ONLY_MESSAGE,
  RECAP_SENT,
} from 'shared/tracker-copy';
import { FieldError, Panel, QuickDates, StepHeading } from './panel';
import {
  HELP_CLASS,
  initials,
  INPUT_CLASS,
  LABEL_CLASS,
  Person,
  readError,
  stageChipClass,
  Touch,
  TrackerData,
} from './helpers';

export type LaterStep =
  | 'booked'
  | 'held'
  | 'recap'
  | 'decision'
  | 'won'
  | 'payment'
  | 'message'
  | 'lost';

export type LogResult = RpxResponse<typeof rpx.tracker.logTouch>;

type Errors = Record<string, string | undefined>;

function useFocusFirst(
  dep: unknown,
  selector = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current?.querySelector(selector) as HTMLElement | null;
    el?.focus();
  }, [dep]);
  return ref;
}

function focusField(field: string) {
  requestAnimationFrame(() => {
    (document.getElementById(`log-${field}`) as HTMLElement | null)?.focus();
  });
}

function ErrorFor({ field, errors }: { field: string; errors: Errors }) {
  return <FieldError id={`log-${field}-error`} message={errors[field]} />;
}

function describedBy(field: string, errors: Errors) {
  return errors[field] ? `log-${field}-error` : undefined;
}

function PanelHeader({ data, person }: { data: TrackerData; person: Person }) {
  return (
    <div class="flex items-center gap-3">
      <span class="inline-flex items-center justify-center size-8 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-xs font-semibold">
        {initials(personName(person))}
      </span>
      <span
        class={`px-2 py-0.5 border rounded-full text-xs font-medium ${stageChipClass(
          data.config,
          person.stageKey,
        )}`}
      >
        {stageLabel(data.config, person.stageKey)}
      </span>
    </div>
  );
}

/** The later step a person's stage points to next. */
export function defaultLaterStep(data: TrackerData, person: Person, touches: Touch[]): LaterStep {
  const role = stageRole(data.config, person.stageKey);
  if (role === 'replied') {
    return 'booked';
  }
  if (role === 'booked') {
    return 'held';
  }
  if (role === 'held') {
    const sorted = sortTouches(touches);
    const heldIndex = sorted.map((t) => stageRole(data.config, t.stageKey)).lastIndexOf('held');
    const recapAfter = sorted
      .slice(heldIndex + 1)
      .some((t) => t.kind === 'note' && t.label === RECAP_SENT);
    return recapAfter ? 'decision' : 'recap';
  }
  if (role === 'decision' && !hasWon(data, touches)) {
    return 'won';
  }
  return 'payment';
}

function hasWon(data: TrackerData, touches: Touch[]) {
  return touches.some((t) => t.kind === 'step' && stageRole(data.config, t.stageKey) === 'won');
}

function useSave(onLogged: (result: LogResult) => void) {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  async function save(
    input: Parameters<typeof rpx.tracker.logTouch>[0]['input'],
    courseId: UUID,
    personId: UUID,
  ) {
    setSaving(true);
    try {
      const result = await rpx.tracker.logTouch({ courseId, personId, input });
      onLogged(result);
    } catch (err) {
      const e = readError(err);
      const field = e.field ?? 'form';
      setErrors({ [field]: e.message });
      focusField(field);
    } finally {
      setSaving(false);
    }
  }
  return { saving, errors, setErrors, save };
}

/** Sends are first touches; later steps follow a reply. */
export function LogTouchPanel({
  data,
  courseId,
  person,
  touches,
  channel,
  initialLater,
  onLogged,
  close,
}: {
  data: TrackerData;
  courseId: UUID;
  person: Person;
  touches: Touch[];
  channel: TrackerChannel;
  initialLater?: LaterStep;
  onLogged: (result: LogResult) => void;
  close: () => void;
}) {
  if (initialLater === 'payment') {
    return (
      <LogPaymentPanel
        data={data}
        courseId={courseId}
        person={person}
        onLogged={onLogged}
        close={close}
      />
    );
  }
  if (channel.flow === 'steps') {
    return (
      <LogStepPanel
        data={data}
        courseId={courseId}
        person={person}
        touches={touches}
        channel={channel}
        onLogged={onLogged}
        close={close}
      />
    );
  }
  return (
    <SequencePanel
      data={data}
      courseId={courseId}
      person={person}
      touches={touches}
      channel={channel}
      initialLater={initialLater}
      onLogged={onLogged}
      close={close}
    />
  );
}

function SequencePanel({
  data,
  courseId,
  person,
  touches,
  channel,
  initialLater,
  onLogged,
  close,
}: {
  data: TrackerData;
  courseId: UUID;
  person: Person;
  touches: Touch[];
  channel: TrackerChannel;
  initialLater?: LaterStep;
  onLogged: (result: LogResult) => void;
  close: () => void;
}) {
  const role = stageRole(data.config, person.stageKey);
  const startOnOutreach = !initialLater && (!role || role === 'contacted');
  const [tab, setTab] = useState<'outreach' | 'later'>(startOnOutreach ? 'outreach' : 'later');
  const name = personName(person);
  return (
    <Panel
      title={`Log touch · ${name}`}
      header={<PanelHeader data={data} person={person} />}
      close={close}
    >
      <div
        role="tablist"
        aria-label="Kind of touch"
        class="flex gap-2 border-b border-gray-200 dark:border-gray-700 -mt-2"
      >
        {(['outreach', 'later'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            class={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t
                ? 'border-indigo-600 text-indigo-700 dark:text-indigo-300'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900'
            }`}
            onClick={() => setTab(t)}
          >
            {t === 'outreach' ? 'Outreach touch' : 'Later step'}
          </button>
        ))}
      </div>
      {tab === 'outreach' ? (
        <OutreachForm
          data={data}
          courseId={courseId}
          person={person}
          onLogged={onLogged}
          close={close}
        />
      ) : (
        <LaterForm
          data={data}
          courseId={courseId}
          person={person}
          channel={channel}
          wonLogged={hasWon(data, touches)}
          initial={initialLater ?? defaultLaterStep(data, person, touches)}
          onLogged={onLogged}
          close={close}
        />
      )}
    </Panel>
  );
}

function Footer({
  formId,
  label,
  saving,
  close,
}: {
  formId: string;
  label: string;
  saving: boolean;
  close: () => void;
}) {
  return (
    <div class="sticky bottom-0 -mx-4 sm:-mx-6 -mb-5 mt-auto bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 flex items-center gap-3">
      <BtnPrimary type="submit" form={formId} isLoading={saving}>
        {label}
      </BtnPrimary>
      <BtnSecondary type="button" onClick={close}>
        Cancel
      </BtnSecondary>
    </div>
  );
}

function OutreachForm({
  data,
  courseId,
  person,
  onLogged,
  close,
}: {
  data: TrackerData;
  courseId: UUID;
  person: Person;
  onLogged: (result: LogResult) => void;
  close: () => void;
}) {
  const { config, today } = data;
  const steps = config.sequence;
  const defaultStep =
    steps.find((s) => s.key === person.nextStepKey) ?? steps.find(isOpeningStep) ?? steps[0];
  const [stepKey, setStepKey] = useState(defaultStep?.key ?? '');
  const [sentOn, setSentOn] = useState(today);
  const [reply, setReply] = useState<'none' | 'interested' | 'no'>('none');
  const [booked, setBooked] = useState(true);
  const [conversationOn, setConversationOn] = useState<string | undefined>();
  const [bookByOn, setBookByOn] = useState<string | undefined>();
  const [nextKey, setNextKey] = useState<string | undefined>();
  const [nextOn, setNextOn] = useState<string | undefined>();
  const { saving, errors, setErrors, save } = useSave(onLogged);
  const ref = useFocusFirst('outreach');

  const step = steps.find((s) => s.key === stepKey);
  const suggestion = step ? nextSequenceStep(config, step.key, sentOn) : undefined;
  const shownNextKey = nextKey ?? suggestion?.key;
  const shownNextOn = nextOn ?? suggestion?.on;
  const shownConversationOn = conversationOn ?? addDays(sentOn, 3);
  const shownBookByOn = bookByOn ?? addDays(sentOn, 2);
  const contacted = channelStageLabel(data, 'contacted');

  function submit(e: Event) {
    e.preventDefault();
    if (sentOn > today) {
      setErrors({ sentOn: FUTURE_DATE_MESSAGE });
      focusField('sentOn');
      return;
    }
    if (reply === 'interested' && booked && shownConversationOn < sentOn) {
      setErrors({ conversationOn: BOOKING_DATE_MESSAGE });
      focusField('conversationOn');
      return;
    }
    setErrors({});
    const nextStep = steps.find((s) => s.key === shownNextKey);
    save(
      {
        type: 'send',
        stepKey,
        sentOn,
        reply,
        conversationOn: reply === 'interested' && booked ? shownConversationOn : undefined,
        next:
          reply === 'interested' && !booked
            ? { step: BOOK_A_CONVERSATION, on: shownBookByOn }
            : reply === 'none'
              ? nextStep
                ? { step: nextStep.name, stepKey: nextStep.key, on: shownNextOn }
                : { step: '' }
              : undefined,
      },
      courseId,
      person.id as UUID,
    );
  }

  return (
    <form id="log-outreach" class="flex flex-col gap-6 grow" onSubmit={submit}>
      <div ref={ref} class="flex flex-col gap-6">
        <section class="flex flex-col gap-3">
          <StepHeading n={1}>What did you send?</StepHeading>
          <label class="flex flex-col gap-1">
            <span class={LABEL_CLASS}>Touch</span>
            <select
              id="log-stepKey"
              class={INPUT_CLASS}
              value={stepKey}
              onChange={(e) => {
                setStepKey(e.currentTarget.value);
                setNextKey(undefined);
                setNextOn(undefined);
              }}
            >
              {steps.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <div class="flex flex-col gap-1">
            <label class={LABEL_CLASS} for="log-sentOn">
              Sent on
            </label>
            <div class="flex items-center gap-3 flex-wrap">
              <input
                id="log-sentOn"
                type="date"
                class={`${INPUT_CLASS} max-w-44 ${errors.sentOn ? 'border-red-500' : ''}`}
                value={sentOn}
                max={today}
                aria-describedby={describedBy('sentOn', errors)}
                onChange={(e) => {
                  setSentOn(e.currentTarget.value);
                  setNextOn(undefined);
                }}
              />
              <QuickDates
                today={today}
                yesterday={addDays(today, -1)}
                onPick={(d) => {
                  setSentOn(d);
                  setNextOn(undefined);
                }}
              />
            </div>
            <ErrorFor field="sentOn" errors={errors} />
          </div>
        </section>
        <section class="flex flex-col gap-3">
          <StepHeading n={2}>Any reply?</StepHeading>
          <div role="radiogroup" aria-label="Any reply?" class="flex flex-col gap-2">
            {(
              [
                ['none', 'No reply yet'],
                ['interested', 'Interested'],
                ['no', 'No, or not now'],
              ] as const
            ).map(([value, label]) => (
              <label key={value} class="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="reply"
                  checked={reply === value}
                  onChange={() => setReply(value)}
                />
                {label}
              </label>
            ))}
          </div>
          <p class={HELP_CLASS}>Any response counts as a reply, including a no.</p>
        </section>
        <section class="flex flex-col gap-3">
          <StepHeading n={3}>What's next?</StepHeading>
          {reply === 'none' &&
            (suggestion || nextKey ? (
              <div class="flex flex-wrap gap-3">
                <label class="flex flex-col gap-1">
                  <span class={LABEL_CLASS}>Next touch</span>
                  <select
                    class={INPUT_CLASS}
                    value={shownNextKey}
                    onChange={(e) => setNextKey(e.currentTarget.value)}
                  >
                    {steps.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label class="flex flex-col gap-1">
                  <span class={LABEL_CLASS}>On</span>
                  <input
                    type="date"
                    class={INPUT_CLASS}
                    value={shownNextOn}
                    min={sentOn}
                    onChange={(e) => setNextOn(e.currentTarget.value)}
                  />
                </label>
              </div>
            ) : (
              <p class="text-sm text-gray-700 dark:text-gray-300">
                Sequence finished. {person.firstName} stays {contacted} with no next touch.
              </p>
            ))}
          {reply === 'interested' && (
            <div class="flex flex-col gap-3">
              <div role="radiogroup" aria-label="Booked?" class="flex flex-col gap-2">
                <label class="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="booked"
                    checked={booked}
                    onChange={() => setBooked(true)}
                  />
                  {channelStageLabel(data, 'booked')}
                </label>
                <label class="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="booked"
                    checked={!booked}
                    onChange={() => setBooked(false)}
                  />
                  Not booked yet
                </label>
              </div>
              {booked ? (
                <div class="flex flex-col gap-2">
                  <p class="text-sm text-gray-700 dark:text-gray-300">
                    Booked on: the day you sent this
                  </p>
                  <label class={LABEL_CLASS} for="log-conversationOn">
                    Conversation on
                  </label>
                  <input
                    id="log-conversationOn"
                    type="date"
                    class={`${INPUT_CLASS} max-w-44 ${errors.conversationOn ? 'border-red-500' : ''}`}
                    value={shownConversationOn}
                    min={sentOn}
                    aria-describedby={describedBy('conversationOn', errors)}
                    onChange={(e) => setConversationOn(e.currentTarget.value)}
                  />
                  <ErrorFor field="conversationOn" errors={errors} />
                </div>
              ) : (
                <label class="flex flex-col gap-1">
                  <span class={LABEL_CLASS}>Book a conversation by</span>
                  <input
                    type="date"
                    class={`${INPUT_CLASS} max-w-44`}
                    value={shownBookByOn}
                    min={sentOn}
                    onChange={(e) => setBookByOn(e.currentTarget.value)}
                  />
                </label>
              )}
            </div>
          )}
          {reply === 'no' && (
            <p class="text-sm text-gray-700 dark:text-gray-300">
              Counts as a reply. Moves to Done as “{channelStageLabel(data, 'replied_no')}”, with no
              more touches.
            </p>
          )}
        </section>
        <ErrorFor field="form" errors={errors} />
      </div>
      <Footer
        formId="log-outreach"
        label={`Log ${step?.name ?? 'touch'}`}
        saving={saving}
        close={close}
      />
    </form>
  );
}

function channelStageLabel(data: TrackerData, role: Parameters<typeof stageForRole>[1]) {
  for (const channel of data.config.channels) {
    const stage = stageForRole(channel, role);
    if (stage) {
      return stage.label;
    }
  }
  return '';
}

function LaterForm({
  data,
  courseId,
  person,
  channel,
  wonLogged,
  initial,
  onLogged,
  close,
}: {
  data: TrackerData;
  courseId: UUID;
  person: Person;
  channel: TrackerChannel;
  wonLogged: boolean;
  initial: LaterStep;
  onLogged: (result: LogResult) => void;
  close: () => void;
}) {
  const { today } = data;
  const [step, setStep] = useState<LaterStep>(initial);
  const [on, setOn] = useState(today);
  const [forOn, setForOn] = useState<string | undefined>();
  const [amount, setAmount] = useState('');
  const { saving, errors, setErrors, save } = useSave(onLogged);
  const ref = useFocusFirst('later', 'input[type="radio"]:checked');
  const label = (role: Parameters<typeof stageForRole>[1]) =>
    stageForRole(channel, role)?.label ?? '';

  const allOptions: Array<{ value: LaterStep; label: string; help?: string }> = [
    { value: 'booked', label: label('booked') },
    { value: 'held', label: label('held') },
    { value: 'recap', label: RECAP_SENT },
    { value: 'decision', label: label('decision') },
    { value: 'won', label: label('won') },
    { value: 'payment', label: 'Payment received' },
    { value: 'message', label: 'Message sent', help: 'Counts as a send' },
    { value: 'lost', label: label('lost') },
  ];
  const options = allOptions.filter((o) => o.value !== 'won' || !wonLogged);
  const chosen = options.find((o) => o.value === step)!;
  const shownForOn =
    forOn ?? (step === 'decision' ? addDays(today, 5) : step === 'booked' ? on : undefined);
  const datedOn = step !== 'decision' && step !== 'lost';

  function choose(value: LaterStep) {
    setStep(value);
    setForOn(undefined);
    setErrors({});
  }

  function submit(e: Event) {
    e.preventDefault();
    const next: Errors = {};
    if (datedOn && on > today) {
      next.on = FUTURE_DATE_MESSAGE;
    }
    if (step === 'booked' && (!shownForOn || shownForOn < on)) {
      next.forOn = BOOKING_DATE_MESSAGE;
    }
    if (step === 'recap' && forOn && forOn < on) {
      next.forOn = 'Pick a decision date on or after the recap.';
    }
    let amountCents: number | undefined;
    if (step === 'won' || step === 'payment') {
      amountCents = amount.trim() ? parseMoneyCents(amount) : undefined;
      if (!amount.trim()) {
        next.amountCents = step === 'won' ? AMOUNT_BOOKED_MESSAGE : AMOUNT_COLLECTED_MESSAGE;
      } else if (amountCents === undefined) {
        next.amountCents = NUMBERS_ONLY_MESSAGE;
      } else if (amountCents <= 0) {
        next.amountCents = step === 'won' ? AMOUNT_BOOKED_MESSAGE : AMOUNT_COLLECTED_MESSAGE;
      }
    }
    const first = Object.keys(next)[0];
    if (first) {
      setErrors(next);
      focusField(first);
      return;
    }
    setErrors({});
    save(
      {
        type: 'later',
        step,
        on: datedOn ? on : undefined,
        forOn:
          step === 'booked' || step === 'decision'
            ? shownForOn
            : step === 'recap'
              ? forOn
              : undefined,
        amountCents,
      },
      courseId,
      person.id as UUID,
    );
  }

  return (
    <form id="log-later" class="flex flex-col gap-6 grow" onSubmit={submit}>
      <div ref={ref} class="flex flex-col gap-6">
        <div role="radiogroup" aria-label="Later step" class="flex flex-col gap-2">
          {options.map((o) => (
            <label key={o.value} class="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="later-step"
                checked={step === o.value}
                onChange={() => choose(o.value)}
              />
              <span>
                {o.label}
                {o.help && <span class="text-gray-500 dark:text-gray-400"> ({o.help})</span>}
              </span>
            </label>
          ))}
        </div>
        {datedOn && (
          <div class="flex flex-col gap-1">
            <label class={LABEL_CLASS} for="log-on">
              {step === 'booked' ? 'Booked on' : 'Date'}
            </label>
            <div class="flex items-center gap-3 flex-wrap">
              <input
                id="log-on"
                type="date"
                class={`${INPUT_CLASS} max-w-44 ${errors.on ? 'border-red-500' : ''}`}
                value={on}
                max={today}
                aria-describedby={describedBy('on', errors)}
                onChange={(e) => setOn(e.currentTarget.value)}
              />
              <QuickDates today={today} yesterday={addDays(today, -1)} onPick={setOn} />
            </div>
            <ErrorFor field="on" errors={errors} />
          </div>
        )}
        {(step === 'booked' || step === 'recap' || step === 'decision') && (
          <div class="flex flex-col gap-1">
            <label class={LABEL_CLASS} for="log-forOn">
              {step === 'booked' ? 'Conversation on' : 'Decision on'}
              {step === 'recap' && (
                <span class="text-gray-500 dark:text-gray-400 font-normal"> (optional)</span>
              )}
            </label>
            <input
              id="log-forOn"
              type="date"
              class={`${INPUT_CLASS} max-w-44 ${errors.forOn ? 'border-red-500' : ''}`}
              value={shownForOn ?? ''}
              min={step === 'decision' ? undefined : on}
              aria-describedby={describedBy('forOn', errors)}
              onChange={(e) => setForOn(e.currentTarget.value || undefined)}
            />
            <ErrorFor field="forOn" errors={errors} />
          </div>
        )}
        {(step === 'won' || step === 'payment') && (
          <div class="flex flex-col gap-1">
            <label class={LABEL_CLASS} for="log-amountCents">
              {step === 'won' ? 'Amount booked' : 'Amount collected'}
            </label>
            <div class="flex items-center gap-2">
              <span class="text-gray-500 dark:text-gray-400">$</span>
              <input
                id="log-amountCents"
                inputMode="decimal"
                class={`${INPUT_CLASS} max-w-44 ${errors.amountCents ? 'border-red-500' : ''}`}
                value={amount}
                aria-describedby={describedBy('amountCents', errors)}
                onInput={(e) => setAmount(e.currentTarget.value)}
              />
            </div>
            <ErrorFor field="amountCents" errors={errors} />
          </div>
        )}
        <ErrorFor field="form" errors={errors} />
        <ErrorFor field="stageKey" errors={errors} />
      </div>
      <Footer formId="log-later" label={`Log ${chosen.label}`} saving={saving} close={close} />
    </form>
  );
}

function LogStepPanel({
  data,
  courseId,
  person,
  touches,
  channel,
  onLogged,
  close,
}: {
  data: TrackerData;
  courseId: UUID;
  person: Person;
  touches: Touch[];
  channel: TrackerChannel;
  onLogged: (result: LogResult) => void;
  close: () => void;
}) {
  const { today } = data;
  const reachedOn = new Map<string, string>();
  for (const t of sortTouches(touches)) {
    if (t.kind === 'step' && t.stageKey) {
      reachedOn.set(t.stageKey, t.happenedOn);
    }
  }
  const firstOpen = channel.stages.find((s) => !reachedOn.has(s.key)) ?? channel.stages[0];
  const [stageKey, setStageKey] = useState(firstOpen?.key ?? '');
  const [on, setOn] = useState(today);
  const [followUpOn, setFollowUpOn] = useState('');
  const { saving, errors, setErrors, save } = useSave(onLogged);
  const ref = useFocusFirst('step');
  const stage = channel.stages.find((s) => s.key === stageKey);

  function submit(e: Event) {
    e.preventDefault();
    if (on > today) {
      setErrors({ on: FUTURE_DATE_MESSAGE });
      focusField('on');
      return;
    }
    setErrors({});
    save(
      { type: 'step', stageKey, on, followUpOn: followUpOn || undefined },
      courseId,
      person.id as UUID,
    );
  }

  return (
    <Panel
      title={`Log step · ${personName(person)}`}
      header={<PanelHeader data={data} person={person} />}
      close={close}
    >
      <form id="log-step" class="flex flex-col gap-6 grow" onSubmit={submit}>
        <div ref={ref} class="flex flex-col gap-6">
          <section class="flex flex-col gap-3">
            <StepHeading n={1}>What happened on {channel.label}?</StepHeading>
            <div
              role="radiogroup"
              aria-label={`What happened on ${channel.label}?`}
              class="flex flex-col gap-2"
            >
              {channel.stages.map((s) => {
                const reached = reachedOn.get(s.key);
                return (
                  <label
                    key={s.key}
                    class={`flex items-center gap-2 text-sm ${reached ? 'text-gray-400' : ''}`}
                  >
                    <input
                      type="radio"
                      name="stage"
                      disabled={!!reached}
                      checked={stageKey === s.key}
                      onChange={() => setStageKey(s.key)}
                    />
                    {reached ? `${s.label} · ${fmtTrackerDate(reached, today)}` : s.label}
                  </label>
                );
              })}
            </div>
          </section>
          <section class="flex flex-col gap-2">
            <StepHeading n={2}>When?</StepHeading>
            <div class="flex items-center gap-3 flex-wrap">
              <input
                id="log-on"
                type="date"
                aria-label="When?"
                class={`${INPUT_CLASS} max-w-44 ${errors.on ? 'border-red-500' : ''}`}
                value={on}
                max={today}
                aria-describedby={describedBy('on', errors)}
                onChange={(e) => setOn(e.currentTarget.value)}
              />
              <QuickDates today={today} yesterday={addDays(today, -1)} onPick={setOn} />
            </div>
            <ErrorFor field="on" errors={errors} />
          </section>
          <section class="flex flex-col gap-2">
            <StepHeading n={3}>
              Follow up on{' '}
              <span class="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
            </StepHeading>
            <input
              id="log-followUpOn"
              type="date"
              aria-label="Follow up on"
              class={`${INPUT_CLASS} max-w-44`}
              value={followUpOn}
              min={on}
              onChange={(e) => setFollowUpOn(e.currentTarget.value)}
            />
            <ErrorFor field="followUpOn" errors={errors} />
          </section>
          <ErrorFor field="form" errors={errors} />
          <ErrorFor field="stageKey" errors={errors} />
        </div>
        <Footer
          formId="log-step"
          label={`Log ${stage?.label ?? 'step'}`}
          saving={saving}
          close={close}
        />
      </form>
    </Panel>
  );
}

/** Log payment: its own panel, so a Won person is never offered Won again. */
function LogPaymentPanel({
  data,
  courseId,
  person,
  onLogged,
  close,
}: {
  data: TrackerData;
  courseId: UUID;
  person: Person;
  onLogged: (result: LogResult) => void;
  close: () => void;
}) {
  const { today } = data;
  const [on, setOn] = useState(today);
  const [amount, setAmount] = useState('');
  const { saving, errors, setErrors, save } = useSave(onLogged);
  const amountRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    amountRef.current?.focus();
  }, []);

  function submit(e: Event) {
    e.preventDefault();
    const amountCents = amount.trim() ? parseMoneyCents(amount) : undefined;
    const next: Errors = {};
    if (on > today) {
      next.on = FUTURE_DATE_MESSAGE;
    }
    if (!amount.trim() || amountCents === 0) {
      next.amountCents = AMOUNT_COLLECTED_MESSAGE;
    } else if (amountCents === undefined) {
      next.amountCents = NUMBERS_ONLY_MESSAGE;
    }
    const first = Object.keys(next)[0];
    if (first) {
      setErrors(next);
      focusField(first);
      return;
    }
    setErrors({});
    save({ type: 'later', step: 'payment', on, amountCents }, courseId, person.id as UUID);
  }

  return (
    <Panel
      title={`Log payment · ${personName(person)}`}
      header={<PanelHeader data={data} person={person} />}
      close={close}
    >
      <form id="log-payment" class="flex flex-col gap-6 grow" onSubmit={submit}>
        <div class="flex flex-col gap-6">
          <div class="flex flex-col gap-1">
            <label class={LABEL_CLASS} for="log-on">
              Date
            </label>
            <div class="flex items-center gap-3 flex-wrap">
              <input
                id="log-on"
                type="date"
                class={`${INPUT_CLASS} max-w-44 ${errors.on ? 'border-red-500' : ''}`}
                value={on}
                max={today}
                aria-describedby={describedBy('on', errors)}
                onChange={(e) => setOn(e.currentTarget.value)}
              />
              <QuickDates today={today} yesterday={addDays(today, -1)} onPick={setOn} />
            </div>
            <ErrorFor field="on" errors={errors} />
          </div>
          <div class="flex flex-col gap-1">
            <label class={LABEL_CLASS} for="log-amountCents">
              Amount collected
            </label>
            <div class="flex items-center gap-2">
              <span class="text-gray-500 dark:text-gray-400">$</span>
              <input
                id="log-amountCents"
                ref={amountRef}
                inputMode="decimal"
                class={`${INPUT_CLASS} max-w-44 ${errors.amountCents ? 'border-red-500' : ''}`}
                value={amount}
                aria-describedby={describedBy('amountCents', errors)}
                onInput={(e) => setAmount(e.currentTarget.value)}
              />
            </div>
            <ErrorFor field="amountCents" errors={errors} />
          </div>
          <ErrorFor field="form" errors={errors} />
        </div>
        <Footer formId="log-payment" label="Log payment" saving={saving} close={close} />
      </form>
    </Panel>
  );
}
